import { useMediaQuery } from '@mui/material';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { breakpoints } from 'src/assets/_responsive-mixin';
import { Icon } from 'src/components/shared/Icon/Icon';
import { getNavItems, isNavItem, NavItem, NavItemChild } from './nav-items';
import {
  Logo,
  SidebarContainer,
  SidebarItem,
  SidebarItemChild,
  SidebarItemChildList,
  SidebarItemList,
  SidebarNav
} from './Sidebar.styles';
import logo from 'src/assets/images/logos/fourc_logo.png';

type SidebarProps = {
  isSidebarOpen: boolean;
  onToggleSidebar: () => void;
};

export const Sidebar = ({ onToggleSidebar, isSidebarOpen }: SidebarProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const navItems = getNavItems();

  const [activeItem, setActiveItem] = useState<Nullable<NavItem>>(null);
  const [activeChild, setActiveChild] = useState<Nullable<NavItemChild>>(null);

  const smallScreen = useMediaQuery(`(max-width: ${breakpoints.small})`);

  const isNavItemActive = (navItem: NavItem, path: string) =>
    (navItem.route && path?.includes(navItem.route)) ||
    (navItem.children &&
      navItem.children.some(child => path.includes(child.route)));

  const initializeSidebar = useCallback(
    (path: string) => {
      navItems.forEach(item => {
        if (isNavItemActive(item, path)) {
          setActiveItem(item);
          setActiveChild(
            item.children?.find(c => path.includes(c.route)) ?? null
          );
        }
      });
    },
    [navItems]
  );

  useEffect(() => {
    initializeSidebar(location.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onNavItemClick = (item: NavItem | NavItemChild) => {
    if (isNavItem(item)) {
      setActiveItem(item);
    } else {
      setActiveChild(item);
    }

    if (item.route) {
      navigate(item.route);

      if (smallScreen) {
        onToggleSidebar();
      }
    }
  };

  return (
    <SidebarContainer className={isSidebarOpen ? 'open' : ''}>
      <SidebarNav>
        <SidebarItemList>
          {navItems
            .filter(item => !item.hidden)
            .map(item => (
              <Fragment key={item.name}>
                <SidebarItem
                  active={activeItem?.name === item.name && !!activeItem.route}
                  disabled={item.disabled}
                  onClick={() => !item.disabled && onNavItemClick(item)}
                >
                  <Icon className="mr-8" type={item.icon} />
                  {t(`${item.name}`)}
                  {!!item.children && (
                    <Icon size={12} type="la-angle-down" className="ml-auto" />
                  )}
                </SidebarItem>

                {!!item.children && (
                  <SidebarItemChildList
                    style={{
                      height:
                        activeItem?.name === item.name
                          ? item.children.length * 56
                          : 0
                    }}
                  >
                    {item.children.map(child => (
                      <SidebarItemChild
                        key={child.name}
                        active={activeChild?.name === child.name}
                        disabled={activeChild?.disabled}
                        onClick={() => onNavItemClick(child)}
                      >
                        {t(`${child.name}`)}
                      </SidebarItemChild>
                    ))}
                  </SidebarItemChildList>
                )}
              </Fragment>
            ))}
        </SidebarItemList>
      </SidebarNav>

      <Logo src={logo} alt="FourC logo" />
    </SidebarContainer>
  );
};
